import { useState, useEffect, useCallback } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { getMenuItems } from '../modules/menu';
import { usePublicConfig } from '../hooks/usePublicConfig';
import { queryClient } from '../lib/queryClient';
import { wordpressKeys } from '../hooks/useWordPress';
import { getPageBySlug } from '../api/client';
import { env } from '../config';
import { ThemeToggle } from '../themes';

export const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { data: publicConfig } = usePublicConfig();
  const menuItems = getMenuItems(publicConfig?.modules, publicConfig?.menu);
  const isHomePage = location.pathname === '/';
  const siteName = publicConfig?.site?.name || env.VITE_SITE_NAME; // runtime site.name, env fallback

  // Split site name for branded styling: first word normal, rest in primary color
  const nameParts = siteName.split(' ');
  const firstWord = nameParts[0];
  const restWords = nameParts.slice(1).join(' ');

  // Scroll listener
  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll(); // Check initial scroll position
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close mobile menu on route change
  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location.pathname]);

  // Prevent body scroll when mobile menu is open
  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isMobileMenuOpen]);

  // Hash link detection for <a href> vs <Link to>
  const isHashLink = (path: string) => path.startsWith('/#') || path.startsWith('#');

  // Active link detection
  const isActive = (path: string) => {
    if (path === '/' || path === '') return location.pathname === '/';
    const normalized = path.startsWith('/') ? path : `/${path}`;
    return location.pathname === normalized;
  };

  // Prefetch WordPress page data on hover for instant navigation
  const handlePrefetch = useCallback((path: string) => {
    const slug = path.replace(/^\//, '') || 'home';
    queryClient.prefetchQuery({
      queryKey: wordpressKeys.page(slug),
      queryFn: () => getPageBySlug(slug),
    });
  }, []);

  // Header styling: glassmorphism normally, transparent on homepage at top
  const showTransparent = isHomePage && !isScrolled;
  const headerClasses = showTransparent
    ? 'bg-transparent border-transparent'
    : 'bg-surface/90 backdrop-blur-md border-b border-border';
  const logoColor = showTransparent ? 'text-white' : 'text-heading';
  const linkColor = showTransparent
    ? 'text-white/90 hover:text-white'
    : 'text-body hover:text-primary';
  const activeLinkColor = showTransparent ? 'text-white font-bold' : 'text-primary';

  const renderNavLink = (item: { title: string; path: string; external?: boolean }, mobile = false) => {
    const active = isActive(item.path);
    const baseClasses = mobile
      ? `block text-base font-semibold transition-colors ${active ? 'text-primary' : 'text-body hover:text-primary'}`
      : `text-sm font-semibold transition-colors ${active ? activeLinkColor : linkColor}`;

    if (item.external) {
      return (
        <a
          key={item.path}
          href={item.path}
          target="_blank"
          rel="noopener noreferrer"
          className={baseClasses}
        >
          {item.title}
        </a>
      );
    }

    if (isHashLink(item.path)) {
      const handleHashClick = (e: React.MouseEvent) => {
        if (mobile) setIsMobileMenuOpen(false);
        const hash = item.path.includes('#') ? item.path.split('#')[1] : '';
        if (!isHomePage) {
          e.preventDefault();
          navigate('/');
          // Poll for the anchor element after home page renders
          const maxAttempts = 20;
          let attempt = 0;
          const interval = setInterval(() => {
            const el = document.getElementById(hash);
            attempt++;
            if (el) {
              clearInterval(interval);
              el.scrollIntoView({ behavior: 'smooth' });
            } else if (attempt >= maxAttempts) {
              clearInterval(interval);
            }
          }, 100);
        }
      };
      return (
        <a
          key={item.path}
          href={item.path}
          className={baseClasses}
          onClick={handleHashClick}
        >
          {item.title}
        </a>
      );
    }

    const to = item.path.startsWith('/') ? item.path : `/${item.path}`;
    return (
      <Link key={item.path} to={to} className={baseClasses} onMouseEnter={() => handlePrefetch(to)}>
        {item.title}
      </Link>
    );
  };

  return (
    <header className={`sticky top-0 z-50 w-full transition-all duration-300 ${headerClasses}`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-20">

          {/* Logo: icon badge + brand text */}
          <Link to="/" className="flex items-center gap-2">
            <div className="bg-primary p-1.5 rounded-lg text-white">
              <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
              </svg>
            </div>
            <span className={`text-xl font-bold tracking-tight uppercase transition-colors duration-300 ${logoColor}`}>
              {firstWord}
              {restWords && <span className="text-primary">{restWords}</span>}
            </span>
          </Link>

          {/* Desktop nav */}
          <nav className="hidden md:flex items-center gap-8">
            {menuItems.map(item => renderNavLink(item))}
          </nav>

          {/* CTA + theme toggle + mobile hamburger */}
          <div className="flex items-center gap-4">
            <ThemeToggle className={showTransparent ? 'text-white' : 'text-body'} />
            <Link
              to="/contact"
              className="hidden md:inline-flex bg-primary text-white px-6 py-2.5 rounded-lg font-bold text-sm hover:bg-primary/90 transition-all shadow-lg shadow-primary/20"
            >
              Get Quote
            </Link>
            <button
              className="md:hidden p-2 -mr-2"
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              aria-label={isMobileMenuOpen ? 'Close menu' : 'Open menu'}
              aria-expanded={isMobileMenuOpen}
            >
              {isMobileMenuOpen ? (
                <svg className={`w-6 h-6 transition-colors ${showTransparent ? 'text-white' : 'text-body'}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                </svg>
              ) : (
                <svg className={`w-6 h-6 transition-colors ${showTransparent ? 'text-white' : 'text-body'}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M4 6h16M4 12h16M4 18h16" />
                </svg>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile menu panel */}
      {isMobileMenuOpen && (
        <div className="md:hidden bg-surface border-t border-border px-4 py-6 space-y-4 shadow-lg">
          {menuItems.map(item => renderNavLink(item, true))}
          <Link
            to="/contact"
            className="block w-full text-center bg-primary text-white px-6 py-2.5 rounded-lg font-bold text-sm hover:bg-primary/90 transition-all shadow-lg shadow-primary/20 mt-4"
            onClick={() => setIsMobileMenuOpen(false)}
          >
            Get Quote
          </Link>
        </div>
      )}
    </header>
  );
};
